// Copyright 2026 AllSpice. All rights reserved.

import type {DiffJson} from './diff_json.ts';

type UnknownRecord = Record<string, unknown>;

type ResolvedInspectorEntity = {
  headEntity: unknown;
  baseEntity: unknown;
};

type InspectorLookup = (
  headObj: Record<string, unknown> | unknown[] | null | undefined,
  baseObj: Record<string, unknown> | unknown[] | null | undefined,
  key: string,
  defaultVal: unknown,
) => unknown;

function isRecord(value: unknown): value is UnknownRecord {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function asLookupContainer(value: unknown): UnknownRecord | unknown[] | null {
  if (Array.isArray(value) || isRecord(value)) {
    return value;
  }

  return null;
}

/**
 * Extract the entity ID from a diff tree path like '/components/entity_id'.
 */
function extractEntityIdFromDiffPath(path: string): string | null {
  const parts = path.split('/').filter(Boolean);
  if (parts.length >= 2) {
    return parts[1]; // '/namespace/id' → id
  }
  return null;
}

function maybeResolveEquivalentBaseEntity(
  baseEntity: unknown,
  baseContainer: unknown,
  dataPath: string[],
  elementId: string,
  diffTree: DiffJson | null | undefined,
): unknown {
  if (baseEntity !== null && baseEntity !== undefined) {
    return baseEntity;
  }

  // Use crysknife diff data as the source of truth for entity matching.
  if (diffTree && dataPath.length === 1) {
    const diffKey = `/${dataPath[0]}/${elementId}`;
    const diffNode = diffTree[diffKey];
    if (diffNode) {
      // Added or removed — crysknife is definitive.
      if ('diff_kind' in diffNode) {
        return null;
      }
      // ChildrenChange: entity is modified. If crysknife matched it to a
      // renamed base entity, it provides base_path.
      if ('base_path' in diffNode && diffNode.base_path) {
        const baseEntityId = extractEntityIdFromDiffPath(diffNode.base_path);
        if (baseEntityId && isRecord(baseContainer)) {
          const baseLookup = (baseContainer as UnknownRecord)[baseEntityId];
          if (baseLookup !== null && baseLookup !== undefined) {
            return baseLookup;
          }
        }
      }
    }
  }

  return null;
}

export function resolveInspectorEntity(
  headContainer: unknown,
  baseContainer: unknown,
  elementId: string,
  dataPath: string[],
  lookup: InspectorLookup,
  diffTree?: DiffJson | null,
): ResolvedInspectorEntity {
  const headEntity = lookup(asLookupContainer(headContainer), {}, elementId, null);
  const baseEntity = lookup(asLookupContainer(baseContainer), {}, elementId, null);

  return {
    headEntity,
    baseEntity: maybeResolveEquivalentBaseEntity(baseEntity, baseContainer, dataPath, elementId, diffTree),
  };
}
